import type { AudienceId } from '@shared/constants/audiences';
import { useAudienceStore, AUDIENCE_COOKIE } from '../stores/audience';
import type { CommuneResult } from '../components/ui/city-select';
import { getZoning, type ClimateZone, type AnahZone } from './zoning';

/** Commune stockée enrichie avec le zoning calculé au moment du stockage. */
export interface StoredLocation extends CommuneResult {
  climateZone: ClimateZone;
  anahZone: AnahZone;
}

/**
 * Helpers d'accès au profil audience.
 *
 * ⚠️ **À PROPOS DE CE FICHIER** :
 * Ces fonctions sont des proxys MINIMALISTES autour de `useAudienceStore`.
 * Tout nouveau code DOIT utiliser :
 *   - `useActiveAudience()` (hook réactif URL + store) — pour le rendu
 *   - `useAudienceStore` (sélecteur Zustand) — pour les actions/lectures précises
 *
 * Ces helpers existent pour la rétro-compat avec le code qui n'a pas encore
 * été migré. Ils seront progressivement supprimés.
 *
 * La gestion de la `location` (commune) reste indépendante du store audience
 * pour le moment — pas de migration urgente, le couplage est faible.
 */

const LOCATION_KEY = 'sef_location';
const LOCATION_SESSION_KEY = 'sef_location_session';
const LOCATION_DAYS = 365;

function hasPreferencesConsent(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const raw = window.localStorage.getItem('sef_consent');
    if (!raw) return false;
    const consent = JSON.parse(raw) as { preferences?: boolean };
    return consent.preferences === true;
  } catch {
    return false;
  }
}

/** @deprecated Utiliser `useActiveAudience()` ou `useAudienceStore`. */
export const AUDIENCE_CHANGED = 'sef:audience-changed';

function setCookie(name: string, value: string, days: number): void {
  if (typeof document === 'undefined') return;
  const expires = new Date(Date.now() + days * 86400000).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
}

function expireCookie(name: string): void {
  if (typeof document === 'undefined') return;
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax`;
}

function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  if (!match || !match[1]) return null;
  return decodeURIComponent(match[1]);
}

/** @deprecated Utiliser `useAudienceStore.getState().audienceId` ou le hook `useStoredAudienceId()`. */
export function getStoredAudience(): AudienceId | null {
  if (typeof window === 'undefined') return null;
  return useAudienceStore.getState().audienceId;
}

/** @deprecated Utiliser `useAudienceStore.getState().setAudience(id)`. */
export function storeAudience(id: AudienceId): void {
  if (typeof window === 'undefined') return;
  useAudienceStore.getState().setAudience(id);
  window.dispatchEvent(new CustomEvent(AUDIENCE_CHANGED, { detail: { id } }));
}

/** @deprecated Utiliser `useAudienceStore.getState().modalSeen`. */
export function hasSeenModal(): boolean {
  if (typeof window === 'undefined') return true;
  return useAudienceStore.getState().modalSeen;
}

/** @deprecated Utiliser `useAudienceStore.getState().markModalSeen()`. */
export function markModalSeen(): void {
  if (typeof window === 'undefined') return;
  useAudienceStore.getState().markModalSeen();
}

/** Réinitialise audience + location. À utiliser depuis la page legal/cookies. */
export function resetAudience(): void {
  if (typeof window === 'undefined') return;
  useAudienceStore.getState().clearAudience();
  expireCookie(AUDIENCE_COOKIE);
  window.localStorage.removeItem(LOCATION_KEY);
  expireCookie(LOCATION_KEY);
}

export function storeLocation(commune: CommuneResult): StoredLocation {
  if (typeof window === 'undefined') return { ...commune, ...getZoning(commune.codeDepartement) };
  const zoning = getZoning(commune.codeDepartement);
  const enriched: StoredLocation = { ...commune, ...zoning };
  const json = JSON.stringify(enriched);
  // Session courante : toujours disponible (nécessaire au parcours en cours).
  try {
    window.sessionStorage.setItem(LOCATION_SESSION_KEY, json);
  } catch {
    // ignore
  }
  // Persistance inter-visites = cookie de préférence (consentement requis).
  if (hasPreferencesConsent()) {
    try {
      window.localStorage.setItem(LOCATION_KEY, json);
      setCookie(LOCATION_KEY, json, LOCATION_DAYS);
    } catch {
      // ignore — ne pas bloquer le parcours si le stockage échoue
    }
  }
  return enriched;
}

export function getStoredLocation(): StoredLocation | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw =
      window.sessionStorage.getItem(LOCATION_SESSION_KEY) ??
      window.localStorage.getItem(LOCATION_KEY) ??
      getCookie(LOCATION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CommuneResult & Partial<StoredLocation>;
    // Rétrocompat : anciennes entrées sans zoning → recalculer
    if (!parsed.climateZone || !parsed.anahZone) {
      return { ...parsed, ...getZoning(parsed.codeDepartement) };
    }
    return parsed as StoredLocation;
  } catch {
    return null;
  }
}
